import React from 'react';
import { Link } from 'react-router-dom';
import type { MemberCareTeamMember, MemberCareTeamRole } from '../../types/memberCareTeam';

interface MemberCareTeamSectionProps {
  members: MemberCareTeamMember[];
}

const OXBLOOD = '#5C1A1C';

/**
 * Fixed two-slot roster, shown in order regardless of how many people have
 * been assigned. Each slot carries a patient-facing title plus an info tooltip
 * describing what that person does day-to-day; the visible body is the person's
 * personal bio (or a pending note while we assign them).
 */
const ROLE_ORDER: MemberCareTeamRole[] = ['coordinator', 'medical_director'];

const ROLE_META: Record<MemberCareTeamRole, { title: string; does: string }> = {
  coordinator: {
    title: 'Relationship Manager',
    does: 'Kit delivery, scheduling, secure messages, and NHS correspondence — your single point of contact between reviews.',
  },
  medical_director: {
    title: 'Senior Care Clinician',
    does: 'Runs your clinical history interview, reads your record, writes and signs off your quarterly reports, and holds your review calls.',
  },
  // Retired role. Kept in the map so a legacy careTeam block still renders a
  // title rather than crashing; it is not in ROLE_ORDER, so it is never a slot.
  care_clinician: {
    title: 'Care clinician',
    does: 'Former care-clinician role, now covered by your Senior Care Clinician.',
  },
};

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function InfoTip({ text }: { text: string }) {
  return (
    <span className="ct-info" tabIndex={0} role="note" aria-label={text}>
      i
      <span className="ct-tip">{text}</span>
    </span>
  );
}

function Avatar({ member }: { member: MemberCareTeamMember | undefined }) {
  // A portrait that fails to load must degrade to the initials tile, not a
  // broken-image icon. The url is a stored Storage link, so it can 403/404 on
  // a bucket whose portraits were never uploaded (or uploaded to another
  // project) — the member should never see the difference.
  const [photoFailed, setPhotoFailed] = React.useState(false);

  React.useEffect(() => {
    setPhotoFailed(false); // retry when the member (and so the url) changes
  }, [member?.photoUrl]);

  if (member?.photoUrl && !photoFailed) {
    return (
      <img
        src={member.photoUrl}
        alt=""
        className="object-cover"
        style={{ width: 56, height: 56 }}
        onError={() => setPhotoFailed(true)}
      />
    );
  }
  if (member) {
    return (
      <div
        className="flex items-center justify-center text-sm font-medium"
        style={{
          width: 56,
          height: 56,
          backgroundColor: 'rgba(220, 218, 211, 0.45)',
          color: OXBLOOD,
        }}
        aria-hidden
      >
        {initials(member.name)}
      </div>
    );
  }
  return (
    <div
      className="flex items-center justify-center"
      style={{
        width: 56,
        height: 56,
        border: '1px dashed rgba(92, 26, 28, 0.3)',
        color: 'rgba(92, 26, 28, 0.45)',
        fontSize: 18,
        letterSpacing: 2,
      }}
      aria-hidden
    >
      ···
    </div>
  );
}

export default function MemberCareTeamSection({ members }: MemberCareTeamSectionProps) {
  const byRole = new Map(members.map((m) => [m.role, m]));

  return (
    <section id="care-team">
      <div
        className="flex items-end justify-between flex-wrap gap-3 pb-3"
        style={{ borderBottom: `2px solid ${OXBLOOD}` }}
      >
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] mb-1" style={{ color: OXBLOOD }}>
            Your care team
          </p>
          <h2
            className="text-3xl font-normal leading-none"
            style={{ fontFamily: "var(--melio-quote)", color: 'var(--melio-ink)' }}
          >
            Advocates, not alerts.
          </h2>
        </div>
        <p
          className="text-xs leading-relaxed md:text-right"
          style={{ color: 'var(--melio-text-muted)', maxWidth: 280 }}
        >
          Your team is assigned within 48 hours of membership confirmation.{' '}
          <Link to="/messages" className="underline" style={{ color: OXBLOOD }}>
            Message Melio →
          </Link>
        </p>
      </div>

      <div
        className="grid grid-cols-1 md:grid-cols-2"
        style={{ border: '1px solid var(--melio-border)', borderTop: 'none' }}
      >
        {ROLE_ORDER.map((role, index) => {
          const member = byRole.get(role);
          const meta = ROLE_META[role];
          return (
            <div
              key={role}
              className={`p-5 ${index < ROLE_ORDER.length - 1 ? 'border-b md:border-b-0 md:border-r' : ''}`}
              style={{ borderColor: 'var(--melio-border)' }}
            >
              <div className="flex items-start justify-between mb-3.5">
                <Avatar member={member} />
                <InfoTip text={meta.does} />
              </div>
              <p
                className="text-[10px] uppercase tracking-[0.18em] mb-1"
                style={{ color: OXBLOOD }}
              >
                {meta.title}
              </p>
              {member ? (
                <p
                  className="text-lg font-normal mb-1.5"
                  style={{ fontFamily: "var(--melio-quote)", color: 'var(--melio-ink)' }}
                >
                  {member.name}
                </p>
              ) : (
                <p
                  className="text-lg font-normal italic mb-1.5"
                  style={{ fontFamily: "var(--melio-quote)", color: 'var(--melio-text-muted)' }}
                >
                  Assigning
                </p>
              )}
              <p className="text-xs leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
                {member?.bio
                  ? member.bio
                  : 'We will introduce them here, with a short note, once your membership is confirmed.'}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}
